import {
	GuestResource,
	RegistrationFormSectionResourceTypeHotelSection,
	RegistrationFormStepChildrenResourceEntityTypeSection,
	RegistrationFormStepResource,
	RuleGuestAttributeResourceTypeRelation,
	RuleResource,
	RuleTypes,
} from "../../../shared/types";
import { HotelSectionFormValues } from "../../../shared/types/hotel";
import { FilledSteps } from "../../provider/registrationFormProviderContext";
import { relationBetween } from "./betweenRule";
import { relationContainsOne } from "./containsOneRule";
import { relationGreaterThanEqual } from "./greaterThanEqualRule";
import { relationHasNotValue } from "./hasNotValueRule";
import { relationHasValue } from "./hasValueRule";
import { relationLessThanEqual } from "./lessThanEqualRule";
import { relationMustContain } from "./mustContainRule";
import { relationMustNotContain } from "./mustNotContainRule";
import { relationNotContainsOne } from "./notContainsOneRule";
import { RelationRuleValidator } from "./types";

const neverValid: RelationRuleValidator<RuleResource<any>> = {
	validate: () => false,
};

const allRelationValidations: {
	[K in RuleTypes]: RelationRuleValidator<RuleResource<RuleGuestAttributeResourceTypeRelation>>;
} = {
	[RuleTypes.IS_REQUIRED]: neverValid,
	[RuleTypes.NOT_UPDATABLE]: neverValid,
	[RuleTypes.READ_ONLY]: neverValid,
	[RuleTypes.HAS_VALUE]: relationHasValue,
	[RuleTypes.HAS_NOT_VALUE]: relationHasNotValue,
	[RuleTypes.HAS_MAX_CHARS]: neverValid,
	[RuleTypes.HAS_MIN_CHARS]: neverValid,
	[RuleTypes.MUST_CONTAIN]: relationMustContain,
	[RuleTypes.MUST_NOT_CONTAIN]: relationMustNotContain,
	[RuleTypes.LESS_THAN_EQUAL]: relationLessThanEqual,
	[RuleTypes.GREATER_THAN_EQUAL]: relationGreaterThanEqual,
	[RuleTypes.CONTAINS_ONE]: relationContainsOne,
	[RuleTypes.NOT_CONTAINS_RULE]: relationNotContainsOne,
	[RuleTypes.IS_EMPTY]: neverValid,
	[RuleTypes.IS_NOT_EMPTY]: neverValid,
	[RuleTypes.REGEX]: neverValid,
	[RuleTypes.BETWEEN]: relationBetween,
};

export const validateRelation = <R extends RuleResource<RuleGuestAttributeResourceTypeRelation>>(
	rule: R,
	guest: GuestResource | null,
	filledSteps: FilledSteps,
	steps: RegistrationFormStepResource[],
): boolean => {
	if (!guest) {
		return false;
	}
	if (
		rule.guestAttribute.entityType === "hotelStay.checkIn" ||
		rule.guestAttribute.entityType === "hotelStay.checkOut"
	) {
		const step = steps
			.flatMap((s) => s.children)
			.find(
				(child) => child.entityType === "section" && child.section.id === rule.sectionId,
			) as RegistrationFormStepChildrenResourceEntityTypeSection<RegistrationFormSectionResourceTypeHotelSection> | null;

		const hotelSections: Record<string, HotelSectionFormValues> | null = Object.entries(filledSteps).flatMap(
			([, filledStep]) => filledStep.hotelSections,
		)[0];

		const dates =
			hotelSections && step && hotelSections[step.section.hotelSettings.hotelStayTypeId]
				? hotelSections[step.section.hotelSettings.hotelStayTypeId].hotelStays.flatMap(
						(stay) => stay[rule.guestAttribute.entityType === "hotelStay.checkIn" ? "checkIn" : "checkOut"],
					)
				: [];

		return allRelationValidations[rule.type].validate(rule, dates);
	}

	const guestAttributeValue = guest.guestAttributeValues.find(({ id }) => id === rule.guestAttribute.id);

	return allRelationValidations[rule.type].validate(rule, guestAttributeValue?.value);
};
